import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router";

/**
 * Protects onboarding work that only exists in memory. Allowed paths are
 * flow-owned routes that preserve the guarded state and must not prompt.
 */
export function useUnsavedProgressNavigationGuard(
  shouldBlock: boolean,
  allowedPaths: readonly string[] = []
) {
  const bypassNextNavigationRef = useRef(false);
  const blocker = useBlocker(
    ({ nextLocation }) => {
      if (bypassNextNavigationRef.current) {
        bypassNextNavigationRef.current = false;
        return false;
      }
      return shouldBlock && !allowedPaths.includes(nextLocation.pathname);
    }
  );

  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlock]);

  const stay = useCallback(() => {
    if (blocker.state === "blocked") blocker.reset();
  }, [blocker]);

  const leaveAnyway = useCallback(() => {
    if (blocker.state === "blocked") blocker.proceed();
  }, [blocker]);

  const bypassNextNavigation = useCallback(() => {
    bypassNextNavigationRef.current = true;
  }, []);

  return {
    isBlocked: blocker.state === "blocked",
    stay,
    leaveAnyway,
    bypassNextNavigation,
  };
}
